import type {
  CloseCurrentSessionLessonContentRequest,
  CloseCurrentSessionQuizContentRequest,
  CourseSessionBlendedResponse,
  CourseSessionCertificateResponse,
  CourseSessionCurrentContentResponse,
  FileResponse,
  CourseDemonstrationContextResponse,
  CourseSessionContentQuizCompleteResponse,
  CourseSessionRequest,
  GetCourseSessionListRequest,
  GetDemonstrationSummaryRequest,
  GetListResponseOfCourseSessionSimpleResponse,
  UpdateCurrentSessionContentRequest,
  UpdateCurrentSessionLessonContentRequest,
} from 'src/api/ProactApiClient';
import {courseSessionApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

export const courseSessionApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getCourseSessionList: builder.query<
      GetListResponseOfCourseSessionSimpleResponse,
      GetCourseSessionListRequest
    >({
      queryFn: clientQuery(params => courseSessionApiClient.list(params)),
      providesTags: ['CourseSessions'],
    }),
    getCourseSession: builder.query<CourseSessionBlendedResponse, number>({
      queryFn: clientQuery(id => courseSessionApiClient.get(id)),
      providesTags: ['CourseSessions'],
    }),
    getSessionCertificate: builder.query<
      CourseSessionCertificateResponse,
      number
    >({
      queryFn: clientQuery(id =>
        courseSessionApiClient.getSessionCertificate(id)
      ),
      providesTags: ['CourseSessions'],
    }),
    getSessionContentCertificate: builder.query<
      CourseSessionCertificateResponse,
      {id: number; contentId: number}
    >({
      queryFn: clientQuery(({id, contentId}) =>
        courseSessionApiClient.getSessionContentCertificate(id, contentId)
      ),
      providesTags: ['CourseSessions'],
    }),
    getSessionCertificatePdf: builder.mutation<
      FileResponse | null,
      {id: number; timeZone: string}
    >({
      queryFn: clientQuery(({id, timeZone}) =>
        courseSessionApiClient.getSessionCertificatePdf(id, timeZone)
      ),
    }),
    getSessionContentCertificatePdf: builder.mutation<
      FileResponse | null,
      {id: number; contentId: number; timeZone: string}
    >({
      queryFn: clientQuery(({id, contentId, timeZone}) =>
        courseSessionApiClient.getSessionContentCertificatePdf(
          id,
          contentId,
          timeZone
        )
      ),
    }),
    getCurrentSessionContent: builder.query<
      CourseSessionCurrentContentResponse,
      number
    >({
      queryFn: clientQuery(sessionId =>
        courseSessionApiClient.getCurrentSessionContent(sessionId)
      ),
      providesTags: ['CourseSessions'],
    }),
    getDemonstrationSummary: builder.query<
      CourseSessionBlendedResponse,
      GetDemonstrationSummaryRequest
    >({
      queryFn: clientQuery(request =>
        courseSessionApiClient.getDemonstrationSummary(request)
      ),
      providesTags: ['CourseSessions'],
    }),
    getDemonstrationContext: builder.query<
      CourseDemonstrationContextResponse,
      GetDemonstrationSummaryRequest
    >({
      queryFn: clientQuery(request =>
        courseSessionApiClient.getDemonstrationContext(request)
      ),
      providesTags: ['CourseSessions'],
    }),
    updateCourseSession: builder.mutation<
      CourseSessionBlendedResponse,
      CourseSessionRequest
    >({
      queryFn: clientQuery(model => courseSessionApiClient.update(model)),
      invalidatesTags: ['CourseSessions', 'TaskAssignments'],
    }),
    updateSessionContentTimeout: builder.mutation<
      boolean,
      UpdateCurrentSessionContentRequest
    >({
      queryFn: clientQuery(request =>
        courseSessionApiClient.updateSessionContentTimeout(request)
      ),
      invalidatesTags: ['TaskAssignments'],
    }),
    updateCurrentSessionLessonContent: builder.mutation<
      boolean,
      UpdateCurrentSessionLessonContentRequest
    >({
      queryFn: clientQuery(request =>
        courseSessionApiClient.updateCurrentSessionLessonContent(request)
      ),
      invalidatesTags: ['TaskAssignments'],
    }),
    closeCurrentSessionLessonContent: builder.mutation<
      CourseSessionCurrentContentResponse,
      CloseCurrentSessionLessonContentRequest
    >({
      queryFn: clientQuery(request =>
        courseSessionApiClient.closeCurrentSessionLessonContent(request)
      ),
      invalidatesTags: ['CourseSessions', 'TaskAssignments', 'CourseAssignments'],
    }),
    closeCurrentSessionQuizContent: builder.mutation<
      CourseSessionContentQuizCompleteResponse,
      CloseCurrentSessionQuizContentRequest
    >({
      queryFn: clientQuery(request =>
        courseSessionApiClient.closeCurrentSessionQuizContent(request)
      ),
      invalidatesTags: ['CourseSessions', 'TaskAssignments', 'CourseAssignments'],
    }),
  }),
});

export const {
  useGetCourseSessionListQuery,
  useLazyGetCourseSessionListQuery,
  useGetCourseSessionQuery,
  useGetSessionCertificateQuery,
  useGetSessionContentCertificateQuery,
  useGetSessionCertificatePdfMutation,
  useGetSessionContentCertificatePdfMutation,
  useGetCurrentSessionContentQuery,
  useGetDemonstrationSummaryQuery,
  useGetDemonstrationContextQuery,
  useUpdateCourseSessionMutation,
  useUpdateSessionContentTimeoutMutation,
  useUpdateCurrentSessionLessonContentMutation,
  useCloseCurrentSessionLessonContentMutation,
  useCloseCurrentSessionQuizContentMutation,
} = courseSessionApi;
